import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrganization } from '../../../context/OrgContext';
import { LuGitBranch } from "react-icons/lu";
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import InviteModal from '../../../components/modals/admin/InviteModal';
import NewWorkflowModal from './NewWorkflowModal';
import AdminLayout from '../../AdminLayout';
import { InterfaceHeader, InterfaceStatsBar, WorkflowGrid } from '../../../components/dashboard';

const InterfaceDashboard = () => {
  const params = useParams();
  const { institution } = useOrganization();
  const navigate = useNavigate();
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  
  // For nested routing, determine the actual interface slug and parent path
  const actualInterfaceSlug = pathSegments[pathSegments.length - 1];
  const parentPath = pathSegments.slice(0, -1).join('/');
  const sectionSlug = pathSegments[pathSegments.length - 2];

  // State management
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showNewWorkflowModal, setShowNewWorkflowModal] = useState(false);

  // Interface and workflows data
  const [section, setSection] = useState(null);
  const [interfaceData, setInterfaceData] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [sectionLoading, setSectionLoading] = useState(true);
  const [interfaceLoading, setInterfaceLoading] = useState(true);
  const [workflowsLoading, setWorkflowsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch section data
  const fetchSection = async () => {
    if (!institution?.id || !sectionSlug) return;

    try {
      setSectionLoading(true);
      
      if (pathSegments.length === 2) {
        // Top-level section - get from institution document
        const institutionRef = doc(db, `colleges/${institution.id}`);
        const institutionDoc = await getDoc(institutionRef);
        const institutionData = institutionDoc.data() || {};
        const sections = institutionData.sections || [];

        const foundSection = sections.find(s => s.slug === sectionSlug);
        setSection(foundSection);
      } else {
        // Nested section - get from parent interface's sections array
        // Navigate up the path to find the section info
        const grandparentSegments = pathSegments.slice(0, -2);
        const parentInterfaceSlug = grandparentSegments[grandparentSegments.length - 1];
        
        // Build path to parent interface
        let parentCollectionPath = `colleges/${institution.id}`;
        for (let i = 0; i < grandparentSegments.length; i += 2) {
          // Add section collection
          parentCollectionPath += `/${grandparentSegments[i]}.section`;
          
          // If there's an interface after this section, add it as a document
          if (i + 1 < grandparentSegments.length) {
            parentCollectionPath += `/${grandparentSegments[i + 1]}`;
          }
        }
        
        const parentInterfacesRef = collection(db, parentCollectionPath);
        const parentInterfacesSnap = await getDocs(parentInterfacesRef);

        let parentInterface = null;
        parentInterfacesSnap.docs.forEach(doc => {
          const data = doc.data();
          if (data.type === 'interface') {
            const titleSlug = data.title ? data.title.toLowerCase()
              .replace(/[^a-z0-9\s-]/g, '')
              .replace(/\s+/g, '-')
              .replace(/-+/g, '-')
              .trim() : '';

            if (titleSlug === parentInterfaceSlug) {
              parentInterface = { id: doc.id, ...data };
            }
          }
        });

        if (parentInterface) {
          const subsections = parentInterface.sections || [];
          const foundSection = subsections.find(s => s.slug === sectionSlug && s.type === 'subsection');
          setSection(foundSection);
        }
      }
    } catch (err) {
      console.error('Error fetching section:', err);
    } finally {
      setSectionLoading(false);
    }
  };

  // Fetch interface data
  const fetchInterface = async () => {
    if (!institution?.id || !sectionSlug || !actualInterfaceSlug) return;

    try {
      setInterfaceLoading(true);
      setError(null);

      // Build the collection path to the parent section
      let collectionPath = `colleges/${institution.id}`;
      const parentSegments = pathSegments.slice(0, -1);
      
      for (let i = 0; i < parentSegments.length; i += 2) {
        // Add section collection
        collectionPath += `/${parentSegments[i]}.section`;
        
        // If there's an interface after this section, add it as a document
        if (i + 1 < parentSegments.length) {
          collectionPath += `/${parentSegments[i + 1]}`;
        }
      }
      
      console.log('Interface collection path:', collectionPath);

      // Query interfaces in the section collection to find by slug
      const interfacesRef = collection(db, collectionPath);
      const interfacesSnap = await getDocs(interfacesRef);

      let foundInterface = null;
      interfacesSnap.docs.forEach(doc => {
        const data = doc.data();
        // Create slug from title for comparison (same logic as used when creating)
        const titleSlug = data.title ? data.title.toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .trim() : '';

        if (titleSlug === actualInterfaceSlug && data.type === 'interface') {
          foundInterface = {
            id: doc.id,
            slug: titleSlug,
            ...data
          };
        }
      });

      if (foundInterface) {
        setInterfaceData(foundInterface);
      } else {
        setError('Interface not found');
      }
    } catch (err) {
      console.error('Error fetching interface:', err);
      setError('Failed to load interface');
    } finally {
      setInterfaceLoading(false);
    }
  };

  // Fetch workflows from interface subcollection
  const fetchWorkflows = async () => {
    if (!institution?.id || !interfaceData?.id) return;

    try {
      setWorkflowsLoading(true);
      
      // Build the path to the interface workflows collection
      let interfaceCollectionPath = `colleges/${institution.id}`;
      const parentSegments = pathSegments.slice(0, -1);
      
      for (let i = 0; i < parentSegments.length; i += 2) {
        // Add section collection
        interfaceCollectionPath += `/${parentSegments[i]}.section`;
        
        // If there's an interface after this section, add it as a document
        if (i + 1 < parentSegments.length) {
          interfaceCollectionPath += `/${parentSegments[i + 1]}`;
        }
      }
      
      const workflowsRef = collection(db, `${interfaceCollectionPath}/${interfaceData.id}/workflows`);
      const workflowsSnap = await getDocs(workflowsRef);

      const workflowsData = workflowsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by creation date (most recent first)
      workflowsData.sort((a, b) => {
        const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return bDate - aDate;
      });

      setWorkflows(workflowsData);
    } catch (error) {
      console.error('Error fetching workflows:', error);
      setWorkflows([]);
    } finally {
      setWorkflowsLoading(false);
    }
  };


  // Fetch data on component mount
  useEffect(() => {
    fetchSection();
    fetchInterface();
  }, [institution?.id, pathSegments.join('/')]);

  // Fetch workflows when interface data is available
  useEffect(() => {
    if (interfaceData) {
      fetchWorkflows();
    }
  }, [interfaceData]);

  // Handle back navigation
  const handleBackClick = () => {
    if (parentPath) {
      navigate(`/${parentPath}`);
    } else {
      navigate('/');
    }
  };

  // Handle settings navigation
  const handleSettingsClick = () => {
    navigate('/settings');
  };

  // Handle invite modal
  const handleInviteClick = () => {
    setShowInviteModal(true);
  };

  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
  };

  // Handle new workflow modal
  const handleNewWorkflowClick = () => {
    setShowNewWorkflowModal(true);
  };

  const handleCloseNewWorkflowModal = () => {
    setShowNewWorkflowModal(false);
  };


  // Handle successful item addition
  const handleItemAdded = () => {
    fetchWorkflows();
    setShowNewWorkflowModal(false);
  };

  // Handle item clicks
  const handleWorkflowClick = (workflow) => {
    const currentPath = pathSegments.join('/');
    navigate(`/${currentPath}/workflows/${workflow.slug || workflow.id}`);
  };



  const isLoading = sectionLoading || interfaceLoading;
  const isDataLoading = workflowsLoading;

  // Loading state
  if (isLoading) {
    return (
      <AdminLayout>
        <div className="h-full w-full text-white px-8 pt-8 flex items-center justify-center">
          <div className="text-center">
            <div className="text-xl">Loading interface...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Error state
  if (error || !interfaceData || !section) {
    return (
      <AdminLayout>
        <div className="h-full w-full text-white px-8 pt-8 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Interface Not Found</h1>
            <p className="text-xl text-gray-400 mb-8">{error || 'The requested interface could not be found.'}</p>
            <button
              onClick={handleBackClick}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Back to {section?.name || 'Section'}
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="h-full w-full text-white px-8 pt-8 flex flex-col overflow-x-clip overflow-y-auto">
        {/* Header */}
        <InterfaceHeader
          title={interfaceData.title}
          onBackClick={handleBackClick}
          onSettingsClick={handleSettingsClick}
          onInviteClick={handleInviteClick}
          onNewClick={handleNewWorkflowClick}
          newButtonText="New Workflow"
        />

        {/* Workflows Count */}
        <InterfaceStatsBar
          itemCount={workflows.length}
          itemType="Workflows"
          createdAt={interfaceData.createdAt}
          isLoading={workflowsLoading}
          icon={LuGitBranch}
          iconColor="green-400"
        />

        {/* Workflows Grid */}
        <div className={`${workflows.length === 0 ? "flex-1" : "h-fit"} w-full flex`}>
          <WorkflowGrid
            workflows={workflows}
            isLoading={isDataLoading}
            onWorkflowClick={handleWorkflowClick}
            onNewWorkflowClick={handleNewWorkflowClick}
          />
        </div>

        {/* Modals */}
        {showInviteModal && (
          <InviteModal
            isOpen={showInviteModal}
            onClose={handleCloseInviteModal}
          />
        )}

        {showNewWorkflowModal && (
          <NewWorkflowModal
            isOpen={showNewWorkflowModal}
            onClose={handleCloseNewWorkflowModal}
            onItemAdded={handleItemAdded}
            pathSegments={pathSegments}
            interfaceId={interfaceData.id}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default InterfaceDashboard;